import React from 'react';
import { Modal } from 'antd';
import UserLicenseConfig from './UserLicenseConfig';
import { UpdateLicenseConfigData } from '../services/admin';

interface LicenseConfigModalProps {
  visible: boolean;
  userId: number;
  config?: any;
  onSave: (data: UpdateLicenseConfigData) => Promise<void>;
  onCancel: () => void;
  currentConfig: any;
  loading?: boolean;
}

const LicenseConfigModal: React.FC<LicenseConfigModalProps> = ({
  visible,
  config,
  onSave,
  onCancel,
  currentConfig,
  loading = false
}) => {
  const parseJson = (value: any, fallback: any) => {
    if (value === undefined || value === null) return fallback;
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      if (value === 'all') return ['all'];
      if (value.trim() === '') return fallback;
      try {
        return JSON.parse(value);
      } catch {
        return fallback;
      }
    }
    return fallback;
  };

  const normalizedConfig = React.useMemo(() => {
    const base = config || currentConfig;
    if (!base) return base;
    return {
      ...base,
      allowedContentTypes: parseJson(base.allowedContentTypes, []),
      restrictedContentTypes: parseJson(base.restrictedContentTypes, []),
      allowWeekdays: parseJson(base.allowWeekdays, [0, 1, 2, 3, 4, 5, 6]),
      allowedRegions: parseJson(base.allowedRegions, []),
      restrictedRegions: parseJson(base.restrictedRegions, []),
      allowedIPs: parseJson(base.allowedIPs, []),
      restrictedIPs: parseJson(base.restrictedIPs, []),
    };
  }, [config, currentConfig]);

  const handleSave = async (data: UpdateLicenseConfigData) => {
    try {
      await onSave(data);
      onCancel(); // Close modal on successful save
    } catch (error) {
      console.error('Save error in modal:', error);
      // Re-throw the error to let parent handle it
      throw error;
    }
  };

  return (
    <Modal
      title={config ? "Edit License Configuration" : "Create License Configuration"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={1000}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      destroyOnClose={true}
    >
      <UserLicenseConfig
        config={normalizedConfig}
        onSave={handleSave}
        loading={loading}
      />
    </Modal>
  );
};

export default LicenseConfigModal;
